/**
 * AccessLogsPage - Epic 38.2
 *
 * Página de Logs de Acesso (Login/Logout/Refresh Token)
 * com filtros multi-select auto-apply, paginação e export XLSX
 */

import React from 'react';
import { MainLayout } from '../components/MainLayout';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { Download, RefreshCw, LogIn, Search, AlertCircle } from 'lucide-react';
import { useAccessLogs } from '../hooks/useAccessLogs';
import { AccessLogsTable } from '../components/accessLogs/AccessLogsTable';
import { AccessLogModal } from '../components/accessLogs/AccessLogModal';

const statusOptions = [
  { value: 'true', label: 'Sucesso' },
  { value: 'false', label: 'Falha' },
];

const AccessLogsPage: React.FC = () => {
  const {
    selectedAcoes, setSelectedAcoes,
    selectedStatus, setSelectedStatus,
    usuarioFilter, setUsuarioFilter,
    ipFilter, setIpFilter,
    acaoOptions,
    handleClearFilters,
    logs,
    loading,
    exporting,
    error,
    currentPage,
    setCurrentPage,
    totalCount,
    totalPages,
    pageSize,
    selectedLog,
    showModal,
    setShowModal,
    handleRefresh,
    handleExport,
    handleViewDetails,
    formatDate,
  } = useAccessLogs();

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <MainLayout>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <LogIn className="text-blue-600 dark:text-blue-400" />
                  Logs de Acesso
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Histórico de login, logout e refresh de tokens
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                  Atualizar
                </button>
                <button
                  onClick={handleExport}
                  disabled={exporting || loading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Download size={16} className={exporting ? 'animate-bounce' : ''} />
                  {exporting ? 'Exportando...' : 'Exportar XLSX'}
                </button>
              </div>
            </div>
          </div>

          {/* Filtros (padrão CalendarioPage - auto-apply) */}
          <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Ação */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ação
                </label>
                <MultiSelectFilter
                  label=""
                  options={acaoOptions}
                  selectedValues={selectedAcoes}
                  onChange={setSelectedAcoes}
                  placeholder="Todas as ações"
                  showTodos={true}
                  showBadges={false}
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <MultiSelectFilter
                  label=""
                  options={statusOptions}
                  selectedValues={selectedStatus}
                  onChange={setSelectedStatus}
                  placeholder="Todos os status"
                  showTodos={true}
                  showBadges={false}
                />
              </div>

              {/* Usuário */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Usuário
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={16} className="text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    value={usuarioFilter}
                    onChange={(e) => setUsuarioFilter(e.target.value)}
                    placeholder="Buscar usuário..."
                    className="w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* IP */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  IP
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={16} className="text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    value={ipFilter}
                    onChange={(e) => setIpFilter(e.target.value)}
                    placeholder="Buscar IP..."
                    className="w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Limpar */}
              <div className="flex items-end">
                <button
                  onClick={handleClearFilters}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Table */}
          <AccessLogsTable
            logs={logs}
            loading={loading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onRowClick={handleViewDetails}
            onPrevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
            onNextPage={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            formatDate={formatDate}
          />

          {/* Detail Modal */}
          {showModal && selectedLog && (
            <AccessLogModal
              log={selectedLog}
              onClose={() => setShowModal(false)}
              formatDate={formatDate}
            />
          )}
        </div>
      </MainLayout>
    </div>
  );
};

export default AccessLogsPage;
